import { useState, useEffect, useRef, ChangeEvent, FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { authHref, clearAuthFrom, rememberAuthFrom, useAuthNextPath } from '../utils/authRedirect.ts';
import './css/RegistrationPage.css';
import './css/LoginPage.css';
import '../components/css/Navigation.css';
import SiteLogo from '../components/SiteLogo.tsx';
import {
  FaEye,
  FaEyeSlash,
  FaBullseye,
  FaCode,
  FaProjectDiagram,
} from 'react-icons/fa';
import { useTranslation } from '../contexts/TranslationContext.tsx';
import { useAuth } from '../contexts/AuthContext.tsx';
import {
  validateRegistration,
  LIMITS,
  USERNAME_FORBIDDEN_WORDS_ERROR_KEY,
  EMAIL_DOMAIN_NOT_ALLOWED_KEY,
} from '../utils/validation.ts';
import { API_BASE } from '../utils/apiEndpoints';
import { useCloseOnEscape } from '../hooks/useCloseOnEscape.ts';
import { startYandexOAuth, startVkOAuth, useYandexOAuthErrorKey } from '../utils/yandexOAuth.ts';
import { AuthTermsCheckbox } from '../components/AuthTermsCheckbox.tsx';
import { NeuralNetBackground } from '../components/NeuralNetBackground.tsx';

const HERO_FEATURES = [
  { icon: FaBullseye, titleKey: 'registration.heroFeature1Title', descKey: 'registration.heroFeature1Desc' },
  { icon: FaCode, titleKey: 'registration.heroFeature2Title', descKey: 'registration.heroFeature2Desc' },
  { icon: FaProjectDiagram, titleKey: 'registration.heroFeature3Title', descKey: 'registration.heroFeature3Desc' },
] as const;

function RegistrationPage() {
  const { t, currentLanguage, setLanguage } = useTranslation();
  const { login } = useAuth();
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
    agreedToTerms: false,
  });
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showEmailDomainModal, setShowEmailDomainModal] = useState(false);
  const [pendingVerification, setPendingVerification] = useState<{ email: string; devLink?: string } | null>(null);
  const [langMenuOpen, setLangMenuOpen] = useState(false);
  const langMenuRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const nextPath = useAuthNextPath();
  const oauthErrorKey = useYandexOAuthErrorKey();

  const languagesToRender = ['ru', 'en'];
  const languageLabel: Record<string, string> = { ru: 'RU', en: 'EN' };

  const closeEmailDomainModal = () => setShowEmailDomainModal(false);
  useCloseOnEscape(showEmailDomainModal, closeEmailDomainModal);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (langMenuRef.current && !langMenuRef.current.contains(e.target as Node)) {
        setLangMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLanguageSelect = (lang: string) => {
    setLanguage(lang);
    setLangMenuOpen(false);
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    const validationErr = validateRegistration({
      username: formData.username,
      email: formData.email,
      password: formData.password,
    });
    if (validationErr) {
      if (validationErr === EMAIL_DOMAIN_NOT_ALLOWED_KEY) {
        setShowEmailDomainModal(true);
        return;
      }
      setError(
        validationErr === USERNAME_FORBIDDEN_WORDS_ERROR_KEY
          ? t(USERNAME_FORBIDDEN_WORDS_ERROR_KEY)
          : validationErr
      );
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError(t('registration.passwordsNotMatch'));
      return;
    }

    if (!formData.agreedToTerms) {
      setError(t('registration.termsNotAccepted'));
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_BASE}/api/registration/registration`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          username: formData.username,
          email: formData.email,
          password: formData.password,
          agreedToTerms: formData.agreedToTerms,
          lang: currentLanguage || 'en',
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        const msg = data.message || data.error || t('registration.failed');
        const translated =
          msg === USERNAME_FORBIDDEN_WORDS_ERROR_KEY
            ? t(USERNAME_FORBIDDEN_WORDS_ERROR_KEY)
            : msg === 'registration.usernameAlreadyTaken'
              ? t('registration.usernameAlreadyTaken')
              : msg === 'registration.emailAlreadyTaken'
                ? t('registration.emailAlreadyTaken')
                : msg === 'registration.emailDomainNotAllowed'
                  ? t('registration.emailDomainNotAllowed')
                  : msg === 'registration.emailSendFailed'
                    ? t('registration.emailSendFailed')
                    : msg === 'registration.emailNotConfigured'
                      ? t('registration.emailNotConfigured')
                      : msg === 'registration.termsNotAccepted'
                        ? t('registration.termsNotAccepted')
                        : msg;
        if (msg === 'registration.emailDomainNotAllowed') {
          setShowEmailDomainModal(true);
          return;
        }
        throw new Error(translated);
      }

      if (data.needsVerification && data.user) {
        rememberAuthFrom(nextPath);
        setPendingVerification({
          email: data.user.email,
          devLink: typeof data.devVerificationLink === 'string' ? data.devVerificationLink : undefined,
        });
        return;
      }

      if (data.success && data.user) {
        login({
          id: String(data.user.id),
          username: data.user.username,
          email: data.user.email,
          role: data.user.role || 'user',
        });
        clearAuthFrom();
        navigate(nextPath);
      } else {
        navigate(authHref('/login', nextPath));
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : t('registration.failed'));
    } finally {
      setIsLoading(false);
    }
  };

  const year = new Date().getFullYear();

  const renderHeader = () => (
    <nav className="sciplex-nav login-page__nav">
      <div className="nav-container login-page__nav-container">
        <div className="nav-logo">
          <SiteLogo />
        </div>

        <div className="nav-links-wrap" aria-hidden />

        <div className="language-switcher" ref={langMenuRef}>
          <button
            type="button"
            className="language-select language-select-btn"
            onClick={() => setLangMenuOpen((v) => !v)}
            aria-expanded={langMenuOpen}
            aria-haspopup="listbox"
            aria-label="Выбор языка"
          >
            <span className="language-select-code">
              {languageLabel[currentLanguage] || currentLanguage.toUpperCase()}
            </span>
          </button>
          <div
            className={`language-dropdown ${langMenuOpen ? 'language-dropdown-open' : ''}`}
            role="listbox"
            aria-label="Языки"
          >
            {languagesToRender.map((lang) => (
              <button
                key={lang}
                type="button"
                role="option"
                aria-selected={currentLanguage === lang}
                className={`language-option ${currentLanguage === lang ? 'language-option-active' : ''}`}
                onClick={() => handleLanguageSelect(lang)}
              >
                <span>{languageLabel[lang] || lang}</span>
              </button>
            ))}
          </div>
        </div>
      </div>
    </nav>
  );

  const renderFooter = () => (
    <footer className="login-page__footer">
      <p className="login-page__copyright">
        © {year} Sciplex. {t('login.footerRights')}
      </p>
      <p className="login-page__legal">
        <Link to="/terms">{t('registration.termsOfService')}</Link>
        <span className="login-page__legal-sep" aria-hidden>
          |
        </span>
        <Link to="/privacy">{t('registration.privacyPolicy')}</Link>
        <span className="login-page__legal-sep" aria-hidden>
          |
        </span>
        <Link to="/terms">{t('login.termsLink')}</Link>
      </p>
    </footer>
  );

  if (pendingVerification) {
    return (
      <div className="login-page registration-page">
        {renderHeader()}
        <div className="login-page__body">
          <NeuralNetBackground />
          <div className="login-page__inner">
          <div className="login-page__grid registration-page__grid--single">
            <div className="login-page__panel">
              <div className="login-card">
                <h2 className="login-card__title">{t('registration.title')}</h2>
                <p className="login-card__subtitle">{t('registration.checkEmail')}</p>
                <p className="registration-pending-email">
                  <strong>{pendingVerification.email}</strong>
                </p>
                {pendingVerification.devLink && (
                  <p className="registration-pending-dev">
                    {t('registration.devLinkHint')}{' '}
                    <a href={pendingVerification.devLink}>{pendingVerification.devLink}</a>
                  </p>
                )}
                <p className="login-card__register">
                  <Link to="/verify-email" className="login-card__register-link">
                    {t('verifyEmail.resendTitle')}
                  </Link>
                  {' · '}
                  <Link to={authHref('/login', nextPath)} className="login-card__register-link">
                    {t('registration.signIn')}
                  </Link>
                </p>
              </div>
            </div>
          </div>
        </div>
        {renderFooter()}
        </div>
      </div>
    );
  }

  return (
    <div className="login-page registration-page">
      {renderHeader()}

      <div className="login-page__body">
        <NeuralNetBackground />
        <div className="login-page__inner">
        <div className="login-page__grid">
          <aside className="login-page__hero">
            <div className="login-hero__noise" aria-hidden />
            <p className="login-hero__tagline">{t('login.heroTagline')}</p>
            <h1 className="login-hero__title">
              <span className="login-hero__title-line login-hero__title-line--light">
                {t('login.heroTitleLine1')}
              </span>
              <span className="login-hero__title-line login-hero__title-line--accent">
                {t('login.heroTitleLine2')}
              </span>
            </h1>
            <p className="login-hero__lead">{t('login.heroDescription')}</p>
            <ul className="registration-hero-features">
              {HERO_FEATURES.map(({ icon: Icon, titleKey, descKey }) => (
                <li key={titleKey} className="registration-hero-feature">
                  <span className="registration-hero-feature__icon" aria-hidden>
                    <Icon />
                  </span>
                  <span className="registration-hero-feature__text">
                    <span className="registration-hero-feature__title">{t(titleKey)}</span>
                    <span className="registration-hero-feature__desc">{t(descKey)}</span>
                  </span>
                </li>
              ))}
            </ul>
          </aside>

          <div className="login-page__panel">
            <div className="login-card">
              <h2 className="login-card__title">{t('registration.title')}</h2>

              {(error || oauthErrorKey) && (
                <div className="login-card__error">{error || (oauthErrorKey ? t(oauthErrorKey) : '')}</div>
              )}

              <form onSubmit={handleSubmit} className="login-form" noValidate>
                <div className="login-field">
                  <label className="login-field__label" htmlFor="reg-username">
                    {t('registration.username')}
                  </label>
                  <div className="login-field__control">
                    <input
                      id="reg-username"
                      type="text"
                      name="username"
                      value={formData.username}
                      onChange={handleChange}
                      required
                      minLength={LIMITS.USERNAME_MIN}
                      maxLength={LIMITS.USERNAME_MAX}
                      className="login-field__input"
                      placeholder={t('registration.usernamePlaceholder')}
                      autoComplete="username"
                      title={`${LIMITS.USERNAME_MIN}-${LIMITS.USERNAME_MAX} символов, латиница, цифры, _`}
                    />
                  </div>
                </div>

                <div className="login-field">
                  <label className="login-field__label" htmlFor="reg-email">
                    {t('registration.email')}
                  </label>
                  <div className="login-field__control">
                    <input
                      id="reg-email"
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      maxLength={LIMITS.EMAIL_MAX}
                      className="login-field__input"
                      placeholder={t('registration.emailPlaceholder')}
                      autoComplete="email"
                    />
                  </div>
                </div>

                <div className="login-field">
                  <label className="login-field__label" htmlFor="reg-password">
                    {t('registration.password')}
                  </label>
                  <div className="login-field__control">
                    <input
                      id="reg-password"
                      type={showPassword ? 'text' : 'password'}
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      required
                      minLength={LIMITS.PASSWORD_MIN}
                      maxLength={LIMITS.PASSWORD_MAX}
                      className="login-field__input login-field__input--with-toggle"
                      placeholder={t('registration.passwordPlaceholder')}
                      autoComplete="new-password"
                    />
                    <button
                      type="button"
                      className="login-field__toggle"
                      onClick={() => setShowPassword(!showPassword)}
                      aria-label={showPassword ? t('registration.hidePassword') : t('registration.showPassword')}
                    >
                      {showPassword ? <FaEyeSlash /> : <FaEye />}
                    </button>
                  </div>
                </div>

                <div className="login-field">
                  <label className="login-field__label login-field__label--nowrap" htmlFor="reg-confirm-password">
                    {t('registration.confirmPassword')}
                  </label>
                  <div className="login-field__control">
                    <input
                      id="reg-confirm-password"
                      type={showConfirmPassword ? 'text' : 'password'}
                      name="confirmPassword"
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      required
                      minLength={LIMITS.PASSWORD_MIN}
                      maxLength={LIMITS.PASSWORD_MAX}
                      className="login-field__input login-field__input--with-toggle"
                      placeholder={t('registration.confirmPasswordPlaceholder')}
                      autoComplete="new-password"
                    />
                    <button
                      type="button"
                      className="login-field__toggle"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      aria-label={
                        showConfirmPassword ? t('registration.hidePassword') : t('registration.showPassword')
                      }
                    >
                      {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                    </button>
                  </div>
                </div>

                <AuthTermsCheckbox
                  id="reg-terms"
                  checked={formData.agreedToTerms}
                  onChange={(checked) => setFormData((prev) => ({ ...prev, agreedToTerms: checked }))}
                  t={t}
                  required
                />

                <div className="login-submit-wrap">
                  <button type="submit" className="login-submit" disabled={isLoading}>
                    <span className="login-submit__label">
                      {isLoading ? t('registration.registeringButton') : t('registration.registerButton')}
                    </span>
                  </button>
                </div>
              </form>

              <div className="login-divider">
                <span>{t('registration.orContinueWith')}</span>
              </div>

              <div className="login-social">
                <button
                  type="button"
                  className="login-social__btn login-social__btn--vk"
                  disabled={!formData.agreedToTerms}
                  onClick={() => {
                    if (!formData.agreedToTerms) {
                      setError(t('registration.termsNotAccepted'));
                      return;
                    }
                    startVkOAuth({ origin: 'register', agreed: true, next: nextPath });
                  }}
                >
                  <img src="/linked/vk.png" alt="" className="login-social__logo" />
                  {t('registration.continueVk')}
                </button>
                <button
                  type="button"
                  className="login-social__btn login-social__btn--yandex"
                  disabled={!formData.agreedToTerms}
                  onClick={() => {
                    if (!formData.agreedToTerms) {
                      setError(t('registration.termsNotAccepted'));
                      return;
                    }
                    startYandexOAuth({ origin: 'register', agreed: true, next: nextPath });
                  }}
                >
                  <img src="/linked/yandex.png" alt="" className="login-social__logo" />
                  {t('registration.continueYandex')}
                </button>
              </div>

              <p className="login-card__register">
                {t('registration.alreadyAccount')}{' '}
                <Link to={authHref('/login', nextPath)} className="login-card__register-link">
                  {t('registration.signIn')}
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>

      {renderFooter()}
      </div>

      {showEmailDomainModal && (
        <div
          className="registration-email-modal-overlay"
          onClick={closeEmailDomainModal}
          role="dialog"
          aria-modal="true"
          aria-labelledby="registration-email-domain-title"
        >
          <div className="registration-email-modal" onClick={(e) => e.stopPropagation()}>
            <div className="registration-email-modal-header">
              <h3 id="registration-email-domain-title">{t('registration.emailDomainModalTitle')}</h3>
              <button
                type="button"
                className="registration-email-modal-close"
                onClick={closeEmailDomainModal}
                aria-label={t('registration.emailDomainModalClose')}
              >
                ×
              </button>
            </div>
            <p className="registration-email-modal-message">{t('registration.emailDomainNotAllowed')}</p>
            <button type="button" className="registration-email-modal-btn" onClick={closeEmailDomainModal}>
              {t('registration.emailDomainModalClose')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default RegistrationPage;
